import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  BookOpen, FileText, ChevronRight, Save, Trash2, Edit2, Download, Info,
  Bold, Italic, Underline, List, ListOrdered, Image, Palette 
} from 'lucide-react';
import { coursesService } from '../services/courses.service';
import { Course, Lesson } from '../types/courses.types';
import { useAuthStore } from '../store/authStore';
import '../styles/notes.css';

interface Note {
  id: string;
  pdf_note: string | null;
  user_note: string | null;
  created_at: string;
  updated_at: string;
  course: { id: string; title: string; icon?: string };
  lesson: { id: string; title: string };
}

export function NotesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  
  // Note details state
  const [currentNote, setCurrentNote] = useState<Note | null>(null);
  const [userNoteText, setUserNoteText] = useState('');
  const [activeTab, setActiveTab] = useState<'pdf' | 'user'>('pdf');
  
  // UI States
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const autoSaveTimeoutRef = useRef<any>(null);
  const profile = useAuthStore((s) => s.profile);

  // Load all courses and notes initially
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const notesList = await coursesService.getNotes();
        setNotes(notesList);

        let coursesList: Course[] = [];
        if (profile?.id) {
          const withProgress = await coursesService.listCoursesWithProgress(profile.id);
          coursesList = withProgress.filter(c => c.status !== 'not_started');
        } else {
          coursesList = await coursesService.listCourses();
        }
        setCourses(coursesList);
      } catch (err: any) {
        console.error('Veriler yüklenemedi:', err);
        setError('Notlar ve dersler yüklenirken bir sorun oluştu.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [profile?.id]);

  const colors = [
    { name: 'Varsayılan', value: 'inherit' },
    { name: 'Siyah', value: '#000000' },
    { name: 'Kırmızı', value: '#EF4444' },
    { name: 'Mavi', value: '#3B82F6' },
    { name: 'Yeşil', value: '#10B981' },
    { name: 'Turuncu', value: '#F59E0B' },
    { name: 'Mor', value: '#8B5CF6' },
    { name: 'Pembe', value: '#EC4899' },
    { name: 'Turkuaz', value: '#06B6D4' }
  ];

  const executeCommand = (command: string, value: string = '') => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setUserNoteText(editorRef.current.innerHTML);
    }
  };

  const handleInsertImage = () => {
    const choice = window.confirm("Görsel eklemek için ne yapmak istersin?\n\nTamam: Bilgisayardan dosya seç\nİptal: İnternet adresi (URL) yapıştır");
    if (choice) {
      document.getElementById('editor-image-upload')?.click();
    } else {
      const url = window.prompt("Görsel adresi (URL) girin:");
      if (url) {
        executeCommand('insertImage', url);
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const imgHtml = `<img src="${event.target.result}" style="max-width: 100%; border-radius: 8px; margin: 8px 0; display: block;" />`;
          executeCommand('insertHTML', imgHtml);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            if (event.target?.result) {
              const imgHtml = `<img src="${event.target.result}" style="max-width: 100%; border-radius: 8px; margin: 8px 0; display: block;" />`;
              executeCommand('insertHTML', imgHtml);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const autoSaveNote = async (text: string, lessonId: string, courseId: string, existingPdf?: string) => {
    try {
      await coursesService.saveNote({
        courseId,
        lessonId,
        userNote: text,
        pdfNote: existingPdf
      });
      // Silent refresh of notes list
      const updatedNotes = await coursesService.getNotes();
      setNotes(updatedNotes);

      // Also update local currentNote so Delete button appears immediately!
      const newlySaved = updatedNotes.find(n => n.lesson?.id === lessonId);
      if (newlySaved) {
        setCurrentNote(newlySaved);
      }
    } catch (err) {
      console.error('Otomatik kaydetme hatası:', err);
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setUserNoteText(html);

      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      if (selectedLesson && selectedCourse) {
        const lessonId = selectedLesson.id;
        const courseId = selectedCourse.id;
        const existingPdf = currentNote?.pdf_note || undefined;

        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveNote(html, lessonId, courseId, existingPdf);
        }, 1500);
      }
    }
  };

  // Sync editor content when lesson or tab changes
  useEffect(() => {
    if (editorRef.current && activeTab === 'user') {
      editorRef.current.innerHTML = userNoteText;
    }
  }, [selectedLesson, activeTab]);

  // Save pending changes on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // When a course is selected, fetch lessons
  const handleSelectCourse = async (course: Course) => {
    if (autoSaveTimeoutRef.current && selectedLesson && selectedCourse) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await autoSaveNote(userNoteText, selectedLesson.id, selectedCourse.id, currentNote?.pdf_note || undefined);
    }

    setSelectedCourse(course);
    setSelectedLesson(null);
    setCurrentNote(null);
    setUserNoteText('');
    setLessons([]);
    try {
      setLoading(true);
      const lessonsList = await coursesService.listLessons(course.id);
      setLessons(lessonsList);
      
      // Auto-select first lesson if available
      if (lessonsList.length > 0) {
        await handleSelectLesson(lessonsList[0], course);
      }
    } catch (err: any) {
      console.error('Dersler yüklenemedi:', err);
      setError('Ders listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  // When a lesson is selected, load existing note or prepare empty note state
  const handleSelectLesson = async (lesson: Lesson, course: Course) => {
    if (autoSaveTimeoutRef.current && selectedLesson && selectedCourse) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await autoSaveNote(userNoteText, selectedLesson.id, selectedCourse.id, currentNote?.pdf_note || undefined);
    }

    setSelectedLesson(lesson);
    
    // Find matching note row
    const existing = notes.find(n => n.lesson?.id === lesson.id);
    
    if (existing) {
      setCurrentNote(existing);
      setUserNoteText(existing.user_note || '');
      // If there's a PDF note, show it. Otherwise show user notes
      if (existing.pdf_note) {
        setActiveTab('pdf');
      } else {
        setActiveTab('user');
      }
    } else {
      setCurrentNote(null);
      setUserNoteText('');
      setActiveTab('user');
    }
    setSuccessMsg(null);
  };

  // Save/Update note
  const handleSaveNote = async () => {
    if (!selectedCourse || !selectedLesson) return;
    try {
      setSaving(true);
      setError(null);
      
      const savedNote = await coursesService.saveNote({
        courseId: selectedCourse.id,
        lessonId: selectedLesson.id,
        userNote: userNoteText,
        pdfNote: currentNote?.pdf_note || undefined // keep existing pdf summary if present
      });

      setSuccessMsg('Not başarıyla kaydedildi! 💾');
      
      // Refresh notes list
      const updatedNotes = await coursesService.getNotes();
      setNotes(updatedNotes);
      
      // Update local currentNote reference
      const newlySaved = updatedNotes.find(n => n.lesson?.id === selectedLesson.id);
      if (newlySaved) {
        setCurrentNote(newlySaved);
      }
      
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      console.error('Not kaydedilemedi:', err);
      setError('Not kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  // Delete note
  const handleDeleteNote = async () => {
    if (!currentNote) return;
    if (!window.confirm('Bu notu tamamen silmek istediğine emin misin?')) return;
    
    try {
      setSaving(true);
      await coursesService.deleteNote(currentNote.id);
      setSuccessMsg('Not başarıyla silindi.');
      
      // Refresh notes list
      const updatedNotes = await coursesService.getNotes();
      setNotes(updatedNotes);
      
      // Reset view state
      setCurrentNote(null);
      setUserNoteText('');
      setActiveTab('user');

      // Clear the rich text editor innerHTML
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      console.error('Not silinemedi:', err);
      setError('Not silinirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  // Download note as Markdown (.md)
  const handleDownloadNote = () => {
    if (!selectedCourse || !selectedLesson) return;
    
    let content = `# ${selectedCourse.title} - ${selectedLesson.title} Notları\n\n`;
    
    if (currentNote?.pdf_note) {
      content += `## 📄 Ders Özeti (Cin Ali Anlatımı)\n\n${currentNote.pdf_note}\n\n`;
    }
    
    if (userNoteText) {
      content += `## ✍️ Benim Notlarım\n\n${userNoteText}\n`;
    }

    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${selectedCourse.title.replace(/\s+/g, '_')}_${selectedLesson.title.replace(/\s+/g, '_')}_notlari.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="notes-page-container">
      {/* Sidebar - Course & Lesson selection */}
      <aside className="notes-sidebar">
        <div className="notes-sidebar-header">
          <BookOpen size={20} className="header-icon" />
          <h2>Derslerim</h2>
        </div>

        <div className="notes-course-list">
          {courses.map((course) => {
            const hasNotesInCourse = notes.some(n => n.course?.id === course.id);
            const isSelected = selectedCourse?.id === course.id;
            
            return (
              <div key={course.id} className={`notes-course-item-wrapper ${isSelected ? 'open' : ''}`}>
                <button 
                  onClick={() => handleSelectCourse(course)}
                  className={`notes-course-btn ${isSelected ? 'active' : ''}`}
                >
                  <span className="course-title-sec">
                    <span className="course-emoji">📚</span>
                    <span className="course-name-text">{course.title}</span>
                  </span>
                  <ChevronRight size={16} className="arrow-icon" />
                </button>
                
                {/* Lessons list inside course (Accordion-style) */}
                {isSelected && (
                  <div className="notes-lesson-list">
                    {loading && lessons.length === 0 ? (
                      <div className="notes-sidebar-loading">Yükleniyor...</div>
                    ) : lessons.length === 0 ? (
                      <div className="notes-sidebar-empty">Ders bulunamadı.</div>
                    ) : (
                      lessons.map((lesson) => {
                        const isLessonSelected = selectedLesson?.id === lesson.id;
                        const hasSavedNote = notes.some(n => n.lesson?.id === lesson.id);
                        
                        return (
                          <button
                            key={lesson.id}
                            onClick={() => handleSelectLesson(lesson, course)}
                            className={`notes-lesson-btn ${isLessonSelected ? 'active' : ''}`}
                          >
                            <FileText size={14} className={hasSavedNote ? 'note-saved-icon' : 'note-empty-icon'} />
                            <span className="lesson-name-text">{lesson.title}</span>
                            {hasSavedNote && <span className="saved-badge">Not</span>}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main Workspace */}
      <main className="notes-workspace">
        {selectedLesson ? (
          <div className="notes-workspace-active">
            <header className="notes-workspace-header">
              <div className="workspace-title-area">
                <span className="workspace-course-tag">{selectedCourse?.title}</span>
                <h1>{selectedLesson.title}</h1>
              </div>
              
              <div className="workspace-actions">
                <button
                  onClick={handleDownloadNote}
                  className="notes-btn secondary"
                  title="Markdown Dosyası Olarak İndir"
                >
                  <Download size={16} />
                  <span>Notu İndir</span>
                </button>

                {currentNote && (
                  <button
                    onClick={handleDeleteNote}
                    className="notes-btn danger"
                    title="Bu notu tamamen sil"
                    disabled={saving}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </header>

            {/* Error and Success Notifications */}
            {error && <div className="notes-alert error">{error}</div>}
            {successMsg && <div className="notes-alert success">{successMsg}</div>}

            {/* Workspace tabs */}
            <div className="notes-tab-bar">
              {currentNote?.pdf_note && (
                <button
                  onClick={() => setActiveTab('pdf')}
                  className={`notes-tab ${activeTab === 'pdf' ? 'active' : ''}`}
                >
                  📄 Cin Ali Özeti (PDF)
                </button>
              )}
              <button
                onClick={() => setActiveTab('user')}
                className={`notes-tab ${activeTab === 'user' ? 'active' : ''}`}
              >
                ✍️ Benim Notlarım
              </button>
            </div>

            {/* Workspace Content */}
            <div className="notes-workspace-content">
              {activeTab === 'pdf' && currentNote?.pdf_note ? (
                <div className="notes-pdf-viewer markdown-body">
                  <ReactMarkdown>{currentNote.pdf_note}</ReactMarkdown>
                </div>
              ) : (
                <div className="notes-editor-container">
                  <div className="editor-toolbar">
                    <div className="toolbar-group">
                      <button 
                        type="button" 
                        onClick={() => executeCommand('bold')} 
                        className="toolbar-btn" 
                        title="Kalın"
                      >
                        <Bold size={16} />
                      </button>
                      <button 
                        type="button" 
                        onClick={() => executeCommand('italic')} 
                        className="toolbar-btn" 
                        title="İtalik"
                      >
                        <Italic size={16} />
                      </button>
                      <button 
                        type="button" 
                        onClick={() => executeCommand('underline')} 
                        className="toolbar-btn" 
                        title="Altı Çizili"
                      >
                        <Underline size={16} />
                      </button>
                    </div>

                    <div className="toolbar-group">
                      <button 
                        type="button" 
                        onClick={() => executeCommand('insertUnorderedList')} 
                        className="toolbar-btn" 
                        title="Madde İşaretli Liste"
                      >
                        <List size={16} />
                      </button>
                      <button 
                        type="button" 
                        onClick={() => executeCommand('insertOrderedList')} 
                        className="toolbar-btn" 
                        title="Numaralı Liste"
                      >
                        <ListOrdered size={16} />
                      </button>
                    </div>

                    <div className="toolbar-group">
                      <button 
                        type="button" 
                        onClick={handleInsertImage} 
                        className="toolbar-btn" 
                        title="Görsel Ekle"
                      >
                        <Image size={16} />
                      </button>
                      <input 
                        type="file" 
                        id="editor-image-upload" 
                        style={{ display: 'none' }} 
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </div>

                    <div className="toolbar-group color-palette">
                      <Palette size={16} className="palette-icon" />
                      <div className="color-options">
                        {colors.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => executeCommand('foreColor', c.value)}
                            className="color-circle"
                            style={{ backgroundColor: c.value === 'inherit' ? 'var(--text-primary, #000)' : c.value }}
                            title={c.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleEditorInput}
                    onPaste={handlePaste}
                    className="notes-editor-body"
                    placeholder="Bu derse dair notlarını buraya yazmaya başla..."
                  />

                  <div className="editor-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span className="auto-save-status" style={{ fontSize: '12.5px', color: 'var(--text-muted, #64748B)', fontWeight: 600 }}>
                      ✓ Değişiklikler otomatik kaydedilir
                    </span>
                    <button
                      onClick={handleSaveNote}
                      className="notes-btn primary"
                      disabled={saving}
                    >
                      <Save size={16} />
                      {saving ? 'Kaydediliyor...' : 'Şimdi Kaydet'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="notes-workspace-empty">
            <div className="empty-state-card">
              <div className="empty-icon-circle">
                <FileText size={48} />
              </div>
              <h2>Kişisel Not Çalışma Alanı</h2>
              <p>
                Sol menüden bir kurs seçip derslerini görüntüleyebilirsin.<br/>
                Ders alırken ders ekranındaki **📄 PDF İndir** butonuna basarsan, 
                Cin Ali AI Öğretmen anlatımı buraya otomatik olarak kaydedilir.
              </p>
              <div className="info-badge">
                💡 İstersen dersleri seçip kendi özel notlarını da ekleyebilir ve güncelleyebilirsin!
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
